import React, { useCallback, useEffect, useState } from 'react';
import { Table, DatePicker, Empty } from 'antd';
import { AuditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import PageHeader from '../../components/PageHeader';
import KpiCards from '../../components/KpiCards';
import axiosInstance from '../../variables/api/axiosInstance';
import { formatDate, formatMontant } from '../../variables/constants';
import { GREEN, Montant } from './commun';
import TableExportButton from '../../components/TableExportButton';

const COULEUR_RETENUE = '#b45309';

const nomMois = (d) => {
  const m = d.format('MMMM YYYY');
  return m.charAt(0).toUpperCase() + m.slice(1);
};

/**
 * Retenues à la source prélevées sur les honoraires d'experts payés dans le mois.
 *
 * Le bureau paie l'expert net de la retenue ; la retenue lui reste en main et doit
 * être reversée à l'État. Cet état liste chaque paiement du mois : expert, taux, retenue.
 * Seuls les règlements réellement PAYÉS comptent : un règlement validé mais pas
 * encore payé n'a encore rien retenu.
 */
function RetenuesAReverser() {
  const { t } = useTranslation('comptabilite');
  const [mois, setMois] = useState(dayjs().startOf('month'));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const charger = useCallback(async () => {
    if (!mois) return;
    setLoading(true);
    try {
      const r = await axiosInstance.get('/v1/comptabilite/fiscalite/retenues-honoraires', {
        params: { annee: mois.year(), mois: mois.month() + 1 },
      });
      setData(r.data?.data || null);
    } catch (e) {
      setData(null);
      toast.error(e?.response?.data?.message || t('fiscalite.messages.chargementRetenuesImpossible'));
    } finally {
      setLoading(false);
    }
  }, [mois, t]);

  // Mois changé : on recharge, il n'y a rien d'autre à attendre.
  useEffect(() => { charger(); }, [charger]);

  const colonnesDetail = [
    { title: t('fiscalite.colonnes.numeroPaiement'), dataIndex: 'numeroPaiement', width: 150 },
    { title: t('fiscalite.colonnes.payeLe'), dataIndex: 'datePaiement', width: 100, format: 'date', render: (v) => formatDate(v) },
    { title: t('fiscalite.colonnes.expert'), dataIndex: 'expert', ellipsis: true },
    { title: t('fiscalite.colonnes.nif'), dataIndex: 'nif', width: 120, render: (v) => v || '—' },
    { title: t('fiscalite.colonnes.taux'), dataIndex: 'taux', width: 90, align: 'center' },
    { title: t('fiscalite.colonnes.sinistre'), dataIndex: 'numeroSinistre', width: 150, render: (v) => v || '—' },
    { title: t('fiscalite.colonnes.cheque'), dataIndex: 'numeroCheque', width: 110, render: (v) => v || '—' },
    { title: t('fiscalite.colonnes.honorairesTtc'), dataIndex: 'honorairesTtc', width: 130, align: 'right', format: 'currency',
      render: (v) => <Montant valeur={v} /> },
    { title: t('fiscalite.colonnes.retenue'), dataIndex: 'retenue', width: 120, align: 'right', format: 'currency',
      render: (v) => <span style={{ color: COULEUR_RETENUE, fontWeight: 600 }}>{formatMontant(v)}</span> },
    { title: t('fiscalite.colonnes.netVerse'), dataIndex: 'netVerse', width: 130, align: 'right', format: 'currency',
      render: (v) => <Montant valeur={v} /> },
  ];

  const vide = data && !data.nombrePaiements;
  const exportable = Boolean(data) && !vide;

  return (
    <>
      <div className="cb-card" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <DatePicker picker="month" value={mois} onChange={(v) => v && setMois(v.startOf('month'))}
          format="MMMM YYYY" allowClear={false} style={{ width: 180 }} />
        <div style={{ flex: 1 }} />
        {/* MEME LIBELLE, MEME ORDRE que Balance, Comptes comptables et Parametrage
            des ecritures : « Exporter Excel » d'abord, « PDF » ensuite. Cet ecran
            disait « Excel » et placait le PDF en premier - seul des cinq ecrans de la
            comptabilite, et le manuel ne pouvait pas decrire les deux a la fois. */}
        {/* UN SEUL BOUTON, AVEC SES OPTIONS. `TableExportButton` pose lui-meme le coin
            bas droit, la reserve de place en bas de page, l'etat desactive et le libelle
            de chargement - tout ce que les deux boutons poses a la main refaisaient a leur
            facon, et pas tout a fait pareil d'un ecran a l'autre. */}
          <TableExportButton
            floating
            data={data?.details || []}
            columns={colonnesDetail}
            disabled={!exportable}
            filename={`retenues_honoraires_${mois.format('YYYY-MM')}`}
            title={t('fiscalite.retenuesAReverser')}
            subtitle={data
              ? t('fiscalite.export.sousTitre', {
                mois: nomMois(mois), nombre: data.nombrePaiements, montant: formatMontant(data.totalRetenues),
              })
              : nomMois(mois)}
            orientation="landscape"
          />
      </div>

      <KpiCards
        columns={3}
        items={[
          { label: t('fiscalite.kpi.paiementsExperts'), value: data ? data.nombrePaiements : '—', color: '#475467' },
          { label: t('fiscalite.kpi.honorairesPayes'), value: data ? formatMontant(data.totalHonoraires) : '—', color: GREEN },
          { label: t('fiscalite.kpi.retenuesAReverserEtat'), value: data ? formatMontant(data.totalRetenues) : '—',
            color: COULEUR_RETENUE, active: Boolean(data?.totalRetenues) },
        ]}
      />

      {vide ? (
        <div className="cb-card">
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('fiscalite.aucunHonoraire', { mois: mois.format('MMMM YYYY') })} />
        </div>
      ) : (
        <div className="cb-card" style={{ padding: 0 }}>
          <Table
            className="cb-table" loading={loading} rowKey="numeroPaiement"
            dataSource={data?.details || []} columns={colonnesDetail}
            scroll={{ x: 1200 }} pagination={{ pageSize: 25, hideOnSinglePage: true }}
            summary={() => (data ? (
              <Table.Summary.Row style={{ background: '#f6faf7', fontWeight: 700 }}>
                <Table.Summary.Cell index={0} colSpan={colonnesDetail.length - 3}>
                  {t('fiscalite.totalMois', { count: data.nombrePaiements })}
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">{formatMontant(data.totalHonoraires)}</Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <span style={{ color: COULEUR_RETENUE }}>{formatMontant(data.totalRetenues)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">{formatMontant(data.totalNetVerse)}</Table.Summary.Cell>
              </Table.Summary.Row>
            ) : null)}
          />
        </div>
      )}
    </>
  );
}


// ───────────────────────────────────────────────────────────────────
// Page
// ───────────────────────────────────────────────────────────────────

/* LES TAUX DE RETENUE NE SONT PLUS ICI. Le referentiel parametrable a rejoint
   Parametres -> Experts : un taux de retenue ne s'applique qu'aux honoraires
   d'experts, et c'est un parametrage, non un etat. Le composant vit desormais dans
   `TauxDeRetenue.js`.

   Il ne reste qu'une vue : les onglets, qui n'avaient de sens qu'a deux, ont
   disparu avec le second. */
export default function Fiscalite() {
  const { t } = useTranslation('comptabilite');
  return (
    <Layout title={t('fiscalite.titre')}>
      <PageHeader titre={t('fiscalite.titre')} icone={<AuditOutlined />} />
      <RetenuesAReverser />
    </Layout>
  );
}
